import { get } from "@/shared/api"
import { API_ENDPOINTS } from "@/shared/api/endpoints"

interface PersonelGetirDto {
  sicilNo: number
  tcKimlikNo: string
  ad: string
  soyad: string
  mobilTel?: string | null
  birimKod?: string | null
  kurumKodGorev?: string | null
  resimUrl?: string | null
  resimBase64?: string | null
  resimMimeType?: string | null
  loginUserRoles?: string[] | null
  orgType?: string[] | null
}

export interface PersonelProfile {
  sicilNo: number
  ad: string
  soyad: string
  mobilTel?: string
  birimKod?: string
  kurumKodGorev?: string
  resimUrl?: string
  resimDataUrl?: string
  loginUserRoles: string[]
  orgType: string[]
}

const GET_USER = API_ENDPOINTS.GET_USER

function toDataUrl(base64?: string | null, mimeType?: string | null): string | undefined {
  if (!base64) return undefined
  const mime = mimeType || "image/jpeg"
  return `data:${mime};base64,${base64}`
}

function readStr(obj: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const val = obj[key]
    if (typeof val === "string" && val.trim() !== "") return val
  }
  return undefined
}

function readNum(obj: Record<string, unknown>, ...keys: string[]): number | undefined {
  for (const key of keys) {
    const val = obj[key]
    if (typeof val === "number" && Number.isFinite(val)) return val
    if (typeof val === "string" && /^\d+$/.test(val.trim())) {
      return Number.parseInt(val.trim(), 10)
    }
  }
  return undefined
}

function readStrArray(obj: Record<string, unknown>, ...keys: string[]): string[] {
  for (const key of keys) {
    const val = obj[key]
    if (Array.isArray(val)) {
      return val.filter((v): v is string => typeof v === "string" && v.trim() !== "")
    }
  }
  return []
}

function mapDtoToPersonelProfile(dto: PersonelGetirDto | Record<string, unknown>): PersonelProfile {
  const raw = dto as Record<string, unknown>
  const sicilNo = readNum(raw, "sicilNo", "SicilNo") ?? 0
  const ad = readStr(raw, "ad", "Ad") ?? ""
  const soyad = readStr(raw, "soyad", "Soyad") ?? ""
  const mobilTel = readStr(raw, "mobilTel", "MobilTel")
  const birimKod = readStr(raw, "birimKod", "BirimKod")
  const kurumKodGorev = readStr(raw, "kurumKodGorev", "KurumKodGorev")
  const resimUrl = readStr(raw, "resimUrl", "ResimUrl")
  const resimBase64 = readStr(raw, "resimBase64", "ResimBase64")
  const resimMimeType = readStr(raw, "resimMimeType", "ResimMimeType")
  const loginUserRoles = readStrArray(raw, "loginUserRoles", "LoginUserRoles")
  const orgType = readStrArray(raw, "orgType", "orgType")

  return {
    sicilNo,
    ad,
    soyad,
    mobilTel,
    birimKod,
    kurumKodGorev,
    resimUrl,
    resimDataUrl: toDataUrl(resimBase64, resimMimeType),
    loginUserRoles,
    orgType
  }
}

export const userProfileService = {
  getCurrentUser: async (): Promise<PersonelProfile | null> => {
    const response = await get<PersonelGetirDto>(GET_USER)
    const data = response.data
    if (!data) return null
    const mapped = mapDtoToPersonelProfile(data as unknown as Record<string, unknown>)
    if (!mapped.sicilNo || mapped.sicilNo <= 0) return null
    return mapped
  },
}
